import { z } from 'zod'
import { ScopeOfWorkEnum } from '@/generated/graphql'

// ============================================================================
// EVENT SCHEMAS
// ============================================================================

const baseEventFormSchema = z.object({
  name: z.string().min(1, 'Select event name'),
  date: z.string()
    .min(1, 'Select a date')
    .refine((date) => {
      const selectedDate = new Date(date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return selectedDate >= today
    }, 'Must be today or later'),
  time: z.string().min(1, 'Select a time'),
  address: z.string().optional(),
  pax: z.preprocess(
    (val) => {
      if (val === '' || val === undefined || val === null) return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    },
    z.union([z.null(), z.number().int().positive()]).optional()
  ),
  rate: z.preprocess(
    (val) => {
      if (val === '' || val === undefined || val === null) return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    },
    z.union([z.null(), z.number().int().positive()]).optional()
  ),
  notes: z.string().optional()
})

export const eventFormSchema = baseEventFormSchema
export type EventFormData = z.infer<typeof eventFormSchema>

export const createOrderEventSchema = baseEventFormSchema.extend({
  id: z.string(),
  isEditing: z.boolean().optional()
})
export type CreateOrderEventData = z.infer<typeof createOrderEventSchema>

// ============================================================================
// ORDER SCHEMAS
// ============================================================================

const contactNoSchema = z.string()
  .min(1, 'Enter contact number')
  .refine((v) => /^\d{10}$/.test(v), 'Must be 10 digits')

const customerValidationSchema = z.object({
  name: z.string().min(1, 'Enter customer name'),
  contactNo: contactNoSchema,
  address: z.string().optional()
})

const orderNameValidation = z.string()
  .min(1, 'Enter order name')
  .min(3, 'At least 3 characters')

export const createOrderSchema = z.object({
  customer: customerValidationSchema,
  orderName: orderNameValidation
})

export type CreateOrderFormData = z.infer<typeof createOrderSchema>

export const updateOrderFormSchema = z.object({
  customer: customerValidationSchema,
  orderName: orderNameValidation
})

export type UpdateOrderFormData = z.infer<typeof updateOrderFormSchema>

// ============================================================================
// MENU MANAGEMENT SCHEMAS
// ============================================================================

export const menuCategorySchema = z.object({
  name: z.string().min(1, 'Enter category name').max(100, 'Name is too long'),
  showCategoryLabel: z.boolean(),
  isInternal: z.boolean(),
})

export type MenuCategoryFormData = z.infer<typeof menuCategorySchema>

export const menuItemSchema = z.object({
  name: z.string().min(1, 'Enter item name').max(100, 'Name is too long'),
  categoryId: z.string().min(1, 'Select a category'),
  vendorIds: z.array(z.string()),
  isExtraChargeable: z.boolean(),
  isExtraByDefault: z.boolean(),
  defaultExtraRate: z.number().positive('Enter a valid rate').nullable().optional(),
  defaultExtraUnit: z.string().max(50).nullable().optional(),
})

export type MenuItemFormData = z.infer<typeof menuItemSchema>

// ============================================================================
// MATERIAL MANAGEMENT SCHEMAS
// ============================================================================

export const materialSchema = z.object({
  name: z.string().min(1, 'Enter material name').max(100, 'Name is too long'),
  categoryId: z.string().min(1, 'Select a category'),
  vendorIds: z.array(z.string()),
})

export type MaterialFormData = z.infer<typeof materialSchema>

// Event Item form schema (for SetupMenuMaterials)
export const eventItemFormSchema = z.object({
  vendorId: z.string().optional().nullable(),
  scopeOfWork: z.enum(Object.values(ScopeOfWorkEnum) as [string, ...string[]]).optional().nullable(),
  serviceDate: z.string().optional().nullable(),
  serviceTime: z.string().optional().nullable(),
  counterCount: z.number().optional().nullable(),
  itemQty: z.number().optional().nullable(),
  itemUnit: z.string().optional().nullable(),
  materials: z.array(z.object({
    masterMaterialId: z.string(),
    materialQty: z.number().optional().nullable(),
    materialUnit: z.string().optional().nullable(),
    vendorId: z.string().optional().nullable(),
  })).optional().superRefine((materials, ctx) => {
    if (!materials) return;
    materials.forEach((material, index) => {
      const hasQty = material.materialQty !== null && material.materialQty !== undefined;
      const hasUnit = material.materialUnit !== null && material.materialUnit !== undefined && material.materialUnit !== '';
      if (hasQty && !hasUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'materialUnit'],
          message: 'Select a unit for this material',
        });
      }
    });
  }),
}).superRefine((data, ctx) => {
  const hasVendorId = data.vendorId !== null && data.vendorId !== undefined && data.vendorId !== '';
  if (hasVendorId && !data.scopeOfWork) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['scopeOfWork'],
      message: 'Select scope of work for this vendor',
    });
  }

  const hasItemQty = data.itemQty !== null && data.itemQty !== undefined;
  const hasItemUnit = data.itemUnit !== null && data.itemUnit !== undefined && data.itemUnit !== '';
  if (hasItemQty && !hasItemUnit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['itemUnit'],
      message: 'Select a unit for item qty',
    });
  }

  const hasServiceDate = data.serviceDate !== null && data.serviceDate !== undefined && data.serviceDate !== '';
  if (hasServiceDate && !data.serviceTime) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['serviceTime'],
      message: 'Select a service time',
    });
  }
})

export type EventItemFormData = z.infer<typeof eventItemFormSchema>

// ============================================================================
// AUTH SCHEMAS
// ============================================================================

export const loginSchema = z.object({
  contactNo: contactNoSchema,
  password: z.string().min(1, 'Enter password'),
})

export type LoginFormData = z.infer<typeof loginSchema>

// ============================================================================
// VENDOR MANAGEMENT SCHEMAS
// ============================================================================

export const vendorSchema = z.object({
  name: z.string().min(1, 'Enter vendor name').max(100, 'Name is too long'),
  contactNo: contactNoSchema,
  scopeOfWork: z.array(z.string()).min(1, 'Select at least one scope of work'),
})

export type VendorFormData = z.infer<typeof vendorSchema>
